import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Calendar,
  Save,
  ArrowLeft,
  Sparkles,
  Swords,
  Gamepad2,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { createLeague as createLeagueMutation } from "../../graphql/mutations";
import {
  DEFAULT_LEAGUE_GAME,
  LEAGUE_GAMES,
  LeagueGame,
  getLeagueFormats,
} from "../../lib/leagueGames";
import LeagueScoringFields from "../../components/LeagueScoringFields";
import { DEFAULT_LEAGUE_SCORING, LeagueScoringConfig } from "../../lib/scoring";

const client = generateClient();

interface LeagueFormData extends LeagueScoringConfig {
  name: string;
  description: string;
  game: LeagueGame;
  format: string;
  startDate: string;
  endDate: string;
  maxParticipants: string;
}

const CreateLeague: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<LeagueFormData>({
    name: "",
    description: "",
    game: DEFAULT_LEAGUE_GAME,
    format: "Standard",
    startDate: "",
    endDate: "",
    maxParticipants: "",
    ...DEFAULT_LEAGUE_SCORING,
  });

  const formatOptions = getLeagueFormats(formData.game);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const leagueInput = {
        name: formData.name,
        description: formData.description || null,
        game: formData.game,
        format: formData.format,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        isActive: true,
        maxParticipants: formData.maxParticipants
          ? parseInt(formData.maxParticipants)
          : null,
        participationPoints: formData.participationPoints,
        winPoints: formData.winPoints,
        drawPoints: formData.drawPoints,
        lossPoints: formData.lossPoints,
        top8FirstPoints: formData.top8FirstPoints,
        top8SecondPoints: formData.top8SecondPoints,
        top8ThirdFourthPoints: formData.top8ThirdFourthPoints,
        top8FifthEighthPoints: formData.top8FifthEighthPoints,
        commanderFirstPoints: formData.commanderFirstPoints,
        commanderSecondPoints: formData.commanderSecondPoints,
        commanderThirdPoints: formData.commanderThirdPoints,
        commanderFourthPoints: formData.commanderFourthPoints,
        premiumMultiplier: formData.premiumMultiplier,
      };

      console.log("Creating league:", leagueInput);

      const result = await client.graphql({
        query: createLeagueMutation,
        variables: {
          input: leagueInput,
        },
      });

      console.log("League created:", result.data.createLeague);

      // Direct GraphQL writes bypass React Query's global mutation cache.
      // Invalidate before navigation so every league list refetches at once.
      await queryClient.invalidateQueries();
      navigate(`/admin/leagues`);
    } catch (error) {
      console.error("Error creating league:", error);
      alert("Failed to create league. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: Exclude<keyof LeagueFormData, "game">,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGameChange = (game: LeagueGame) => {
    const formats = getLeagueFormats(game);
    setFormData((prev) => ({
      ...prev,
      game,
      format: formats[0],
    }));
  };

  const handleScoringChange = (
    field: keyof LeagueScoringConfig,
    value: number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="container mx-auto max-w-3xl p-0 sm:p-2">
      <div className="mb-8 flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/leagues")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leagues
        </Button>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
            League setup
          </p>
          <h1 className="text-3xl font-bold tracking-tight">Create a league</h1>
        </div>
      </div>

      <Card className="overflow-hidden border-white/10 bg-card/80 shadow-2xl shadow-black/20 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            League Information
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Choose a game first and the available formats will update
            automatically.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Game *</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
                  const option = LEAGUE_GAMES[game];
                  const Icon =
                    game === "MAGIC_THE_GATHERING"
                      ? Sparkles
                      : game === "RIFTBOUND"
                      ? Swords
                      : Gamepad2;
                  const selected = formData.game === game;

                  return (
                    <button
                      key={game}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => handleGameChange(game)}
                      className={`group rounded-2xl border p-4 text-left transition-all ${
                        selected
                          ? "border-primary bg-primary/15 shadow-lg shadow-primary/10"
                          : "border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]"
                      }`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            selected
                              ? "bg-primary text-white"
                              : "bg-white/[0.06] text-muted-foreground"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <span
                          className={`h-3 w-3 rounded-full border ${
                            selected
                              ? "border-primary bg-primary ring-4 ring-primary/20"
                              : "border-white/30"
                          }`}
                        />
                      </div>
                      <span className="block font-semibold">
                        {option.label}
                      </span>
                      <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                        {option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">League Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("name", e.target.value)
                  }
                  placeholder={
                    formData.game === "RIFTBOUND"
                      ? "e.g., Riftbound League Night"
                      : formData.game === "DRAGON_BALL_MASTERS"
                      ? "e.g., Dragon Ball Masters League"
                      : "e.g., Standard Showdown League"
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="format">Format *</Label>
                <Select
                  value={formData.format}
                  onValueChange={(value) => handleInputChange("format", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {formatOptions.map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe the league, rules, prizes, etc."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("endDate", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxParticipants">Max Participants</Label>
                <Input
                  id="maxParticipants"
                  type="number"
                  min="1"
                  value={formData.maxParticipants}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("maxParticipants", e.target.value)
                  }
                  placeholder="No limit"
                />
              </div>
            </div>

            <LeagueScoringFields
              value={formData}
              onChange={handleScoringChange}
              isCommander={formData.format === "Commander"}
            />

            <div className="flex justify-end gap-3 border-t border-white/10 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/leagues")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? "Creating..." : "Create League"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateLeague;
